/**
 * Single source of truth for "are this activity's signups closed?". Shared by
 * the Activities card, the Schedule render paths, and the schedule detail modal
 * so the public UI can't drift from the server's own gate
 * (see _logic/actions/_shared/load-slot-and-activity.ts).
 */

import { formatDateShort } from "@/lib/utils/formatting";
import type { ActivityRow, ScheduleItemRow } from "../types/db-types";

/**
 * True when an activity's signups are closed — either explicitly toggled off
 * (`signups_closed`) or past the scheduled cutoff (`signup_closes_at`).
 *
 * @param activity - The activity's signup-closure fields
 * @returns Whether signups are currently closed
 */
export function isActivitySignupClosed(activity: Pick<ActivityRow, "signups_closed" | "signup_closes_at">): boolean {
  if (activity.signups_closed) return true;
  if (activity.signup_closes_at == null) return false;
  // A malformed cutoff (NaN) fails open (signups stay open) rather than throwing — make that explicit.
  const closesAt = new Date(activity.signup_closes_at).getTime();
  return Number.isFinite(closesAt) && closesAt < Date.now();
}

/**
 * True when an activity is published and therefore visible on public surfaces.
 * An activity is published when it has no scheduled `publish_at`, or that time
 * has already passed. A future `publish_at` means the organizer scheduled it to
 * appear later, so it must stay hidden from EVERY public surface — the public
 * Activities render and the chatbot's grounding context alike.
 *
 * A malformed `publish_at` (NaN) fails closed (treated as unpublished/hidden),
 * matching the public render gate and erring toward not leaking draft content.
 *
 * @param activity - The activity's `publish_at` field
 * @param now - Reference instant, defaulting to the current time (injectable for tests)
 * @returns Whether the activity is currently published
 */
export function isActivityPublished(activity: Pick<ActivityRow, "publish_at">, now: Date = new Date()): boolean {
  if (!activity.publish_at) return true;
  return new Date(activity.publish_at).getTime() <= now.getTime();
}

/**
 * True when a schedule item's linked activity has its signups closed, read from
 * the denormalized `activity_signups_closed` / `activity_signup_closes_at`
 * fields populated by `listScheduleItemsPublic`. Returns false for items with no
 * linked activity, or when the fields are absent (builder mode → treated as open).
 *
 * @param item - The schedule item (only its activity-link + denormalized closure fields are read)
 * @returns Whether the linked activity's signups are closed
 */
export function isScheduleItemSignupClosed(
  item: Pick<ScheduleItemRow, "activity_id" | "activity_signups_closed" | "activity_signup_closes_at">
): boolean {
  if (item.activity_id == null) return false;
  return isActivitySignupClosed({
    signups_closed: item.activity_signups_closed ?? false,
    signup_closes_at: item.activity_signup_closes_at ?? null,
  });
}

/**
 * A day-cap or exclusion-group block, as computed client-side by {@link evaluateRuleBlock}.
 * No payload beyond the reason type — the group name is admin-authored free text and isn't
 * shown in this hint (it's never surfaced anywhere in the public UI), so it isn't fetched.
 */
export type RuleBlockReason = "day_cap" | "exclusion_group";

/** Configured day caps (by date) and exclusion groups for one event, as needed by {@link evaluateRuleBlock}. */
export interface SignupLimitRules {
  dayCapsByDate: ReadonlyMap<string, number>;
  exclusionGroups: readonly { activityIds: readonly number[] }[];
}

/** Stable "nothing configured" default — a shared reference so consumers can use it before a fetch resolves. */
export const EMPTY_SIGNUP_LIMIT_RULES: SignupLimitRules = { dayCapsByDate: new Map(), exclusionGroups: [] };

/**
 * Pure client-side hint for the two signup-limit hard blocks (day cap, exclusion group) —
 * additive to the existing signup-closed/full/already-registered checks, not a replacement.
 * Mirrors (a simplified, client-computable version of) the DB's signup_block_reason(), which
 * remains authoritative: this only ever changes what a viewer sees BEFORE they attempt to sign
 * up, never what's actually allowed — a stale/missed hint here still gets caught server-side.
 *
 * @param activityId - The activity the viewer is looking at.
 * @param slotDate - The specific time slot's date, or null for a dateless slot (no day cap can apply).
 * @param viewerRegisteredActivityIds - Activities the viewer holds a `registered` (not waitlisted) row
 *   for in this event — must be status-filtered the same way the DB rule filters, or a merely-waitlisted
 *   registration would falsely block an exclusion-group sibling the server would actually allow.
 * @param registeredCountByDate - date -> count of the viewer's own `registered` rows on that date.
 * @param rules - The event's configured day caps + exclusion groups.
 * @returns The block reason, or null if nothing blocks this slot.
 */
export function evaluateRuleBlock(
  activityId: number,
  slotDate: string | null,
  viewerRegisteredActivityIds: ReadonlySet<number>,
  registeredCountByDate: ReadonlyMap<string, number>,
  rules: SignupLimitRules
): RuleBlockReason | null {
  for (const group of rules.exclusionGroups) {
    if (!group.activityIds.includes(activityId)) continue;
    if (group.activityIds.some((id) => id !== activityId && viewerRegisteredActivityIds.has(id))) {
      return "exclusion_group";
    }
  }

  if (slotDate) {
    const limit = rules.dayCapsByDate.get(slotDate);
    if (limit !== undefined) {
      const current = registeredCountByDate.get(slotDate) ?? 0;
      if (current >= limit) {
        return "day_cap";
      }
    }
  }

  return null;
}

/** Joins activity titles into an English list: "A", "A and B", or "A, B, and C". */
function joinTitles(titles: readonly string[]): string {
  return new Intl.ListFormat("en", { style: "long", type: "conjunction" }).format(titles);
}

/**
 * Specific, human-readable explanation for why the viewer can't register for `activityId` —
 * only meaningful once the caller has already established every bookable slot is blocked (see
 * {@link evaluateRuleBlock} / useActivityRuleState's `byActivityId`). Names the conflicting
 * activity/activities for an exclusion-group block, or the date + limit for a day-cap block,
 * instead of the short generic {@link RuleBlockReason} label.
 *
 * @param activityId - The activity the viewer is looking at.
 * @param bookableSlotDates - Dates of the activity's bookable slots (day-cap check only).
 * @param activityTitleById - Title lookup for every activity in the event (for naming conflicts).
 * @param viewerRegisteredActivityIds - Same status-filtered set evaluateRuleBlock expects.
 * @param registeredCountByDate - Same shape evaluateRuleBlock expects.
 * @param rules - The event's configured day caps + exclusion groups.
 * @returns A specific explanation, or null if nothing blocks this activity.
 */
export function describeRuleBlock(
  activityId: number,
  bookableSlotDates: readonly (string | null)[],
  activityTitleById: ReadonlyMap<number, string>,
  viewerRegisteredActivityIds: ReadonlySet<number>,
  registeredCountByDate: ReadonlyMap<string, number>,
  rules: SignupLimitRules
): string | null {
  for (const group of rules.exclusionGroups) {
    if (!group.activityIds.includes(activityId)) continue;
    const conflictingIds = group.activityIds.filter((id) => id !== activityId && viewerRegisteredActivityIds.has(id));
    if (conflictingIds.length === 0) continue;
    const conflictingTitles = conflictingIds.map((id) => activityTitleById.get(id) ?? "another activity");
    const allTitles = group.activityIds.map((id) => activityTitleById.get(id) ?? "this activity");
    return `You already signed up for ${joinTitles(conflictingTitles)}. You can only sign up for one of: ${joinTitles(allTitles)}.`;
  }

  for (const date of bookableSlotDates) {
    if (!date) continue;
    const limit = rules.dayCapsByDate.get(date);
    if (limit === undefined) continue;
    const current = registeredCountByDate.get(date) ?? 0;
    if (current >= limit) {
      const activityWord = limit === 1 ? "activity" : "activities";
      return `You've already signed up for the maximum of ${limit} ${activityWord} on ${formatDateShort(date)}.`;
    }
  }

  return null;
}
